/**
 * Canonical DE/EN text for the three legal / technical documents surfaced
 * under "Whitepaper & Legal Documents". Single source of truth so the modal,
 * any future print view and the PDF exports never drift apart.
 */
export type DocId = "tokenomics" | "legal" | "audit";
export type DocLang = "de" | "en";

export interface DocSection {
  heading: string;
  /** Paragraphs of body copy. */
  body?: string[];
  /** Optional key/value facts rendered as a definition grid. */
  facts?: { label: string; value: string }[];
  /** Optional bullet list. */
  bullets?: string[];
}

export interface LegalDocument {
  title: string;
  subtitle: string;
  version: string;
  updated: string;
  sections: DocSection[];
  /** Downloadable PDF counterpart. */
  pdf: string;
}

export const LEGAL_DOCUMENTS: Record<DocId, Record<DocLang, LegalDocument>> = {
  tokenomics: {
    en: {
      title: "Executive Tokenomics Summary",
      subtitle: "MEAL2 & MEAL4 — supply, treasury and distribution logic",
      version: "v1.0",
      updated: "Original Release: July 04, 2021",
      pdf: "/docs/executive-tokenomics-summary-de.pdf",
      sections: [
        {
          heading: "1. Dual-Token Architecture",
          body: [
            "The SFN Network runs on two complementary tokens. MEAL2 is the daily utility voucher used to redeem a meal at partner kitchens. MEAL4 is the long-term inflation shield that funds infrastructure and stabilises the purchasing power behind each voucher.",
          ],
        },
        {
          heading: "2. MEAL2 — The Daily Utility",
          facts: [
            { label: "Treasury allocation", value: "250,000 MEAL2" },
            { label: "Role", value: "Redeemable meal voucher" },
            { label: "Reference value", value: "1 meal ≈ 30 THB ≈ 1 USD" },
            { label: "Mint authority", value: "Revoked" },
            { label: "Freeze authority", value: "Revoked" },
          ],
          body: [
            "MEAL2 is issued against a fixed treasury of 250,000 tokens. Circulation is capped by that treasury; new vouchers are only released as verified partner kitchens join the pilot network.",
          ],
        },
        {
          heading: "3. MEAL4 — The Inflation Shield",
          facts: [
            { label: "Maximum supply", value: "1,000,000,000 MEAL4" },
            { label: "Treasury allocation", value: "100,000,000 MEAL4" },
            { label: "Role", value: "Infrastructure & stabilisation reserve" },
            { label: "Distribution", value: "Milestone-gated protocol sale" },
          ],
          body: [
            "MEAL4 carries a hard maximum supply of 1,000,000,000 tokens. 100,000,000 MEAL4 are held in the project treasury and released only against published funding milestones — never through discretionary issuance.",
          ],
        },
        {
          heading: "4. Use of Proceeds",
          bullets: [
            "Kitchen hardware, solar units and cold-chain equipment for pilot sites",
            "Reforestation and regenerative agriculture programmes",
            "Protocol operations, audits and compliance",
          ],
        },
        {
          heading: "5. Transparency Commitments",
          body: [
            "All treasury movements settle on Solana and are publicly verifiable. Supply parameters, mint and freeze authority states are published on-chain and mirrored in the live verification panel on this site.",
          ],
        },
      ],
    },
    de: {
      title: "Executive Tokenomics Zusammenfassung",
      subtitle: "MEAL2 & MEAL4 — Angebot, Treasury und Verteilungslogik",
      version: "v1.0",
      updated: "Erstveröffentlichung: 04. Juli 2021",
      pdf: "/docs/executive-tokenomics-summary-de.pdf",
      sections: [
        {
          heading: "1. Zwei-Token-Architektur",
          body: [
            "Das SFN Network basiert auf zwei sich ergänzenden Token. MEAL2 ist der tägliche Utility-Gutschein, mit dem eine Mahlzeit in Partnerküchen eingelöst wird. MEAL4 ist der langfristige Inflationsschutz, der Infrastruktur finanziert und die Kaufkraft hinter jedem Gutschein stabilisiert.",
          ],
        },
        {
          heading: "2. MEAL2 — Der tägliche Utility-Token",
          facts: [
            { label: "Treasury-Zuteilung", value: "250.000 MEAL2" },
            { label: "Funktion", value: "Einlösbarer Mahlzeiten-Gutschein" },
            { label: "Referenzwert", value: "1 Mahlzeit ≈ 30 THB ≈ 1 USD" },
            { label: "Mint-Autorität", value: "Widerrufen" },
            { label: "Freeze-Autorität", value: "Widerrufen" },
          ],
          body: [
            "MEAL2 wird gegen eine feste Treasury von 250.000 Token ausgegeben. Der Umlauf ist durch diese Treasury begrenzt; neue Gutscheine werden ausschließlich freigegeben, wenn verifizierte Partnerküchen dem Pilotnetzwerk beitreten.",
          ],
        },
        {
          heading: "3. MEAL4 — Der Inflationsschutz",
          facts: [
            { label: "Maximales Angebot", value: "1.000.000.000 MEAL4" },
            { label: "Treasury-Zuteilung", value: "100.000.000 MEAL4" },
            { label: "Funktion", value: "Infrastruktur- und Stabilisierungsreserve" },
            { label: "Verteilung", value: "Meilenstein-gebundener Protokollverkauf" },
          ],
          body: [
            "MEAL4 besitzt ein hartes Maximalangebot von 1.000.000.000 Token. 100.000.000 MEAL4 liegen in der Projekt-Treasury und werden ausschließlich gegen veröffentlichte Finanzierungsmeilensteine freigegeben — niemals durch willkürliche Emission.",
          ],
        },
        {
          heading: "4. Mittelverwendung",
          bullets: [
            "Küchenausstattung, Solareinheiten und Kühlkette für Pilotstandorte",
            "Aufforstung und regenerative Landwirtschaftsprogramme",
            "Protokollbetrieb, Audits und Compliance",
          ],
        },
        {
          heading: "5. Transparenzverpflichtungen",
          body: [
            "Alle Treasury-Bewegungen werden auf Solana abgewickelt und sind öffentlich überprüfbar. Angebotsparameter sowie Mint- und Freeze-Status sind on-chain veröffentlicht und werden im Live-Verifizierungsbereich dieser Seite gespiegelt.",
          ],
        },
      ],
    },
  },

  legal: {
    en: {
      title: "Thailand Gastronomy Legal Framework",
      subtitle: "Utility voucher classification, local operations and AML",
      version: "v1.0",
      updated: "Compliance statement",
      pdf: "/docs/thailand-gastronomy-legal-framework.pdf",
      sections: [
        {
          heading: "1. Classification — Utility Voucher",
          body: [
            "MEAL2 and MEAL4 are issued as utility tokens: prepaid vouchers redeemable for a defined food service. They grant no equity, no profit share, no dividend and no claim on the issuer's assets, and are therefore not offered as investment tokens under Thai SEC digital-asset rules.",
          ],
          bullets: [
            "Consumption right only — redeemable against one meal at a partner kitchen",
            "No expectation of profit from the efforts of a third party",
            "No secondary-market listing is operated or promoted by the issuer",
          ],
        },
        {
          heading: "2. Thai SEC Utility Voucher Standards",
          body: [
            "The project aligns with the utility-voucher standards applied by the Securities and Exchange Commission of Thailand: the token must be usable at issuance, must correspond to a real deliverable good or service, and must be described in transparent, non-promissory language. All public materials follow this standard.",
          ],
        },
        {
          heading: "3. Local Business Operations — the Thailand Sandbox",
          body: [
            "Pilot operations run in the Thailand pilot district. Partner kitchens operate under their own Thai food-business registrations and municipal food-hygiene licensing. The protocol acts solely as the voucher and settlement layer; food preparation, sale and local tax obligations remain with the licensed local operator.",
          ],
        },
        {
          heading: "4. AML / KYC Guidelines",
          bullets: [
            "Partner kitchens and payout wallets are onboarded with identity verification before any treasury settlement",
            "Treasury outflows are milestone-bound and recorded on-chain for audit",
            "Screening against sanctions and high-risk jurisdiction lists prior to onboarding",
            "Anomalous redemption patterns are flagged and settlement is paused pending review",
          ],
        },
        {
          heading: "5. Disclaimer",
          body: [
            "This document is an operational compliance statement, not legal or investment advice. Regulatory treatment of digital assets may change; the framework is reviewed whenever applicable Thai regulation is updated.",
          ],
        },
      ],
    },
    de: {
      title: "Rechtsrahmen Gastronomie Thailand",
      subtitle: "Utility-Gutschein-Einstufung, lokaler Betrieb und Geldwäscheprävention",
      version: "v1.0",
      updated: "Compliance-Erklärung",
      pdf: "/docs/thailand-gastronomy-legal-framework.pdf",
      sections: [
        {
          heading: "1. Einstufung — Utility-Gutschein",
          body: [
            "MEAL2 und MEAL4 werden als Utility-Token ausgegeben: vorausbezahlte Gutscheine, die gegen eine definierte Verpflegungsleistung eingelöst werden. Sie gewähren keine Beteiligung, keinen Gewinnanteil, keine Dividende und keinen Anspruch auf das Vermögen des Emittenten und werden daher nicht als Investment-Token im Sinne der thailändischen SEC-Regeln für digitale Vermögenswerte angeboten.",
          ],
          bullets: [
            "Reines Nutzungsrecht — einlösbar gegen eine Mahlzeit in einer Partnerküche",
            "Keine Gewinnerwartung aus der Tätigkeit Dritter",
            "Der Emittent betreibt und bewirbt keine Sekundärmarkt-Listings",
          ],
        },
        {
          heading: "2. Thailändische SEC-Standards für Utility-Gutscheine",
          body: [
            "Das Projekt orientiert sich an den Utility-Gutschein-Standards der thailändischen Wertpapier- und Börsenaufsicht (SEC): Der Token muss bereits bei Ausgabe nutzbar sein, einer real lieferbaren Ware oder Dienstleistung entsprechen und in transparenter, nicht renditeversprechender Sprache beschrieben werden. Alle öffentlichen Materialien folgen diesem Standard.",
          ],
        },
        {
          heading: "3. Lokaler Geschäftsbetrieb — the Thailand Sandbox",
          body: [
            "Der Pilotbetrieb erfolgt im dem thailändischen Pilotbezirk. Partnerküchen arbeiten mit eigener thailändischer Gewerbeanmeldung für Lebensmittelbetriebe und kommunaler Hygienelizenz. Das Protokoll fungiert ausschließlich als Gutschein- und Abwicklungsschicht; Zubereitung, Verkauf und lokale Steuerpflichten verbleiben beim lizenzierten lokalen Betreiber.",
          ],
        },
        {
          heading: "4. AML- / KYC-Richtlinien",
          bullets: [
            "Partnerküchen und Auszahlungs-Wallets werden vor jeder Treasury-Abwicklung identitätsgeprüft",
            "Treasury-Abflüsse sind meilensteingebunden und on-chain prüfbar dokumentiert",
            "Abgleich mit Sanktionslisten und Hochrisiko-Jurisdiktionen vor dem Onboarding",
            "Auffällige Einlösemuster werden markiert, die Abwicklung wird bis zur Prüfung ausgesetzt",
          ],
        },
        {
          heading: "5. Haftungsausschluss",
          body: [
            "Dieses Dokument ist eine operative Compliance-Erklärung und keine Rechts- oder Anlageberatung. Die regulatorische Behandlung digitaler Vermögenswerte kann sich ändern; der Rahmen wird bei jeder Aktualisierung der einschlägigen thailändischen Regulierung überprüft.",
          ],
        },
      ],
    },
  },

  audit: {
    en: {
      title: "Cryptographic Security Audit Report",
      subtitle: "Contract authorities, treasury controls and anti-bot defence",
      version: "v1.0",
      updated: "Audit result: PASS",
      pdf: "/docs/cryptographic-security-audit-report.pdf",
      sections: [
        {
          heading: "1. Result Summary",
          facts: [
            { label: "Overall status", value: "PASS" },
            { label: "Score", value: "98.4%" },
            { label: "Critical issues", value: "0" },
            { label: "High issues", value: "0" },
            { label: "Scope", value: "Token authorities, treasury flow, web surface" },
          ],
        },
        {
          heading: "2. Token Authority Verification",
          bullets: [
            "MEAL4 — mint and freeze authority state published and verifiable on-chain",
            "MEAL2 — mint authority revoked, freeze authority revoked",
            "Supply parameters mirrored live in the on-page verification panel",
          ],
        },
        {
          heading: "3. Treasury Controls — Multi-Sig",
          bullets: [
            "Treasury outflows require multi-signature approval; no single key can move funds",
            "Signer set is separated from day-to-day operational keys",
            "Disbursements are bound to published funding milestones",
            "Every settlement produces a public, explorer-verifiable transaction",
          ],
        },
        {
          heading: "4. Anti-Bot & Abuse Protection",
          body: [
            "Public forms are protected by Cloudflare Turnstile with server-side token verification, combined with rate limiting per address and heuristic spam scoring. Automated submissions are rejected before they reach storage or email dispatch.",
          ],
        },
        {
          heading: "5. Web & Data Surface",
          bullets: [
            "Row-level security enforced on all stored records; no public read of contact data",
            "Privacy-first analytics with Do-Not-Track and GPC respected",
            "No private keys, seed phrases or secrets are ever requested or stored",
          ],
        },
        {
          heading: "6. Residual Notes",
          body: [
            "The remaining 1.6% consists of low-severity hardening recommendations with no user-fund impact. They are tracked and scheduled for the next release cycle.",
          ],
        },
      ],
    },
    de: {
      title: "Kryptografischer Sicherheitsaudit-Bericht",
      subtitle: "Vertragsautoritäten, Treasury-Kontrollen und Bot-Abwehr",
      version: "v1.0",
      updated: "Auditergebnis: BESTANDEN",
      pdf: "/docs/cryptographic-security-audit-report.pdf",
      sections: [
        {
          heading: "1. Ergebnisübersicht",
          facts: [
            { label: "Gesamtstatus", value: "BESTANDEN (PASS)" },
            { label: "Bewertung", value: "98,4 %" },
            { label: "Kritische Findings", value: "0" },
            { label: "Hohe Findings", value: "0" },
            { label: "Prüfumfang", value: "Token-Autoritäten, Treasury-Fluss, Web-Oberfläche" },
          ],
        },
        {
          heading: "2. Prüfung der Token-Autoritäten",
          bullets: [
            "MEAL4 — Mint- und Freeze-Status veröffentlicht und on-chain überprüfbar",
            "MEAL2 — Mint-Autorität widerrufen, Freeze-Autorität widerrufen",
            "Angebotsparameter werden live im Verifizierungsbereich der Seite gespiegelt",
          ],
        },
        {
          heading: "3. Treasury-Kontrollen — Multi-Sig",
          bullets: [
            "Treasury-Abflüsse erfordern eine Multi-Signatur-Freigabe; kein Einzelschlüssel kann Mittel bewegen",
            "Der Signierkreis ist von den operativen Alltagsschlüsseln getrennt",
            "Auszahlungen sind an veröffentlichte Finanzierungsmeilensteine gebunden",
            "Jede Abwicklung erzeugt eine öffentlich im Explorer prüfbare Transaktion",
          ],
        },
        {
          heading: "4. Bot- und Missbrauchsschutz",
          body: [
            "Öffentliche Formulare sind durch Cloudflare Turnstile mit serverseitiger Token-Prüfung geschützt, ergänzt um Rate-Limiting pro Adresse und heuristisches Spam-Scoring. Automatisierte Einsendungen werden abgewiesen, bevor sie Speicher oder E-Mail-Versand erreichen.",
          ],
        },
        {
          heading: "5. Web- und Datenoberfläche",
          bullets: [
            "Row-Level-Security auf allen gespeicherten Datensätzen; kein öffentlicher Lesezugriff auf Kontaktdaten",
            "Datenschutzfreundliche Analytik, Do-Not-Track und GPC werden respektiert",
            "Es werden niemals private Schlüssel, Seed-Phrasen oder Geheimnisse abgefragt oder gespeichert",
          ],
        },
        {
          heading: "6. Restliche Hinweise",
          body: [
            "Die verbleibenden 1,6 % bestehen aus Härtungsempfehlungen geringer Schwere ohne Auswirkung auf Nutzerguthaben. Sie sind erfasst und für den nächsten Release-Zyklus eingeplant.",
          ],
        },
      ],
    },
  },
};

export function resolveDocLang(lang: string): DocLang {
  return lang === "de" ? "de" : "en";
}
